import { compareGroups, diffGroups, fieldPairs, recordGroups, type DiffGroup } from '../../domain/entityDiff.js';
import type { EntityType } from '../../domain/entityTypes.js';
import { adoptableFields } from '../../domain/fields.js';
import { isActionable, type SyncStatus } from '../../domain/status.js';

/**
 * What the field diff shows for the focused environment — pure, so each empty case says the
 * one thing that is true instead of one sentence hedging across all of them.
 *
 * - `pick` — no environment chosen.
 * - `create` — `missing`: master targets it and it has no copy; the panel previews the record a
 *   push would create there, as rendered for that environment (its per-environment values).
 * - `compare` — both sides exist: the field-by-field diff (or "every synced field matches").
 * - `excluded` / `unknown` / `env_only` — nothing to diff, each for its own reason.
 * - `absent` — anything else with a side missing (e.g. a record not rendered yet).
 */
export type FieldDiffMode = 'pick' | 'create' | 'compare' | 'excluded' | 'unknown' | 'env_only' | 'absent';

export const fieldDiffMode = (
    focus: string | undefined,
    focused: { status: SyncStatus; desired: unknown; actual: unknown } | undefined,
): FieldDiffMode => {
    if (!focus) return 'pick';
    if (!focused) return 'absent';
    if (focused.status === 'missing') return focused.desired ? 'create' : 'absent';
    if (focused.status === 'excluded' || focused.status === 'unknown' || focused.status === 'env_only') {
        return focused.status;
    }
    return focused.desired && focused.actual ? 'compare' : 'absent';
};

/** The focused environment, as the drawer's detail query returns it — what the view reads. */
export type FocusedEnvironment = {
    status: SyncStatus;
    desired: Record<string, unknown> | null;
    actual: Record<string, unknown> | null;
    diffs: { field: string; desired: unknown; actual: unknown }[] | null;
};

/** Everything the field diff draws for one environment; `FieldDiffPanel` only renders it. */
export type FieldDiffView = {
    mode: FieldDiffMode;
    /** `create`: the whole record a push would create there. */
    createGroups: DiffGroup[] | null;
    /** `compare`: the differing fields (`changed`) or every field (`all`); null otherwise, or before `diffs` arrive. */
    groups: DiffGroup[] | null;
    /** How many fields differ — the caption's N. */
    changed: number;
    /** On a `drift` environment, the fields that differ: each gets an **Adopt ▾**. */
    diffFields: Set<string>;
    /** The per-environment ones among them — **Adopt all N as overrides**; empty where the host cannot override. */
    overridable: string[];
    /** Drift or missing: a push would write here, so the full-screen review is worth offering. */
    actionable: boolean;
};

export const fieldDiffView = (
    type: EntityType,
    focus: string | undefined,
    focused: FocusedEnvironment | undefined,
    view: 'changed' | 'all',
    canOverride: boolean,
): FieldDiffView => {
    const mode = fieldDiffMode(focus, focused);
    const createGroups = mode === 'create' && focused?.desired ? recordGroups(type, focused.desired) : null;
    const groups =
        mode !== 'compare' || !focused
            ? null
            : view === 'all'
              ? compareGroups(type, fieldPairs(type, focused.actual, focused.desired))
              : focused.diffs
                ? diffGroups(
                      type,
                      focused.diffs.map((d) => ({ field: d.field, before: d.actual, after: d.desired })),
                  )
                : null;
    const diffFields =
        focus && focused?.status === 'drift' ? new Set(focused.diffs?.map((d) => d.field) ?? []) : new Set<string>();
    return {
        mode,
        createGroups,
        groups,
        changed: focused?.diffs?.length ?? 0,
        diffFields,
        overridable: canOverride ? adoptableFields(type, [...diffFields]) : [],
        actionable: Boolean(focused && isActionable(focused.status)),
    };
};
